/**
 * Editing the axes a module ships, rather than one instance's.
 *
 * The ⇄ in the axis overview fixes the robot on the screen; this fixes the
 * module, so the next one placed is already right. Saving rewrites the
 * module's URDF -- for the shared catalogue that is a diff in the assets
 * repository, and it is admin-only for the same reason the module's YAML
 * is -- so the marks are held here until Save rather than applied as they
 * are made, and the panel says what the file will read afterwards.
 */

import { useEffect, useRef } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertTriangle, IconArrowsExchange, IconGitCommit } from "@tabler/icons-react";

import {
  useInstances,
  useModuleAxesEditable,
  useModuleAxesInstanceId,
  useModuleAxesError,
  useModuleAxesJoints,
  useModuleAxesModuleId,
  useModuleAxesOpen,
  useModuleAxesPending,
  useModuleAxesFollowed,
  useModuleAxesWrittenPath,
  useViewerStore,
} from "../../store";
import { useWebSocket } from "../../WebSocket";
import { useViewerStore as viewerStore } from "../../store";
import type { ModuleAxisData } from "../../types";

/**
 * The name this module joint answers to once it is placed.
 *
 * The editor talks about the module's own joints, the axis overlay about
 * the instance's; they are the same joints under `prefix/joint`. Mirrors
 * ``AssemblyInstance.get_prefix``: an unset prefix is the instance id, an
 * empty one is empty on purpose.
 */
function uniqueJointName(prefix: string | null, instanceId: string, jointName: string): string {
  return `${prefix ?? instanceId}/${jointName}`;
}

/** `0 0 1` rather than `0.000, 0.000, 1.000`: a module axis is written by hand. */
function formatAxis(axis: [number, number, number]): string {
  return axis.map((value) => (Object.is(value, -0) ? 0 : value).toString()).join(" ");
}

function reversedAxis(axis: [number, number, number]): [number, number, number] {
  return [-axis[0] || 0, -axis[1] || 0, -axis[2] || 0];
}

function JointRow({
  joint,
  pending,
  editable,
  onToggle,
  onHover,
}: {
  joint: ModuleAxisData;
  pending: boolean;
  editable: boolean;
  onToggle: (jointName: string) => void;
  onHover: (jointName: string | null) => void;
}) {
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      gap="xs"
      // Which joint is which is a question about the robot, not the list:
      // pointing at a row rings that axis in the viewport and names it.
      onMouseEnter={() => onHover(joint.joint_name)}
      onMouseLeave={() => onHover(null)}
    >
      <Box style={{ minWidth: 0 }}>
        <Text size="xs" fw={500} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {joint.joint_name}
        </Text>
        <Text size="xs" c="dimmed" ff="monospace">
          {pending ? (
            <>
              {formatAxis(joint.axis)} → <span style={{ color: "var(--mantine-color-orange-6)" }}>
                {formatAxis(reversedAxis(joint.axis))}
              </span>
            </>
          ) : (
            formatAxis(joint.axis)
          )}
        </Text>
      </Box>
      <Button
        size="compact-xs"
        variant={pending ? "filled" : "default"}
        color="orange"
        disabled={!editable}
        leftSection={<IconArrowsExchange size={12} />}
        onClick={() => onToggle(joint.joint_name)}
      >
        Reverse
      </Button>
    </Group>
  );
}

export function ModuleAxesPanel() {
  const open = useModuleAxesOpen();
  const moduleId = useModuleAxesModuleId();
  const joints = useModuleAxesJoints();
  const editable = useModuleAxesEditable();
  const pending = useModuleAxesPending();
  const error = useModuleAxesError();
  const writtenPath = useModuleAxesWrittenPath();
  const followed = useModuleAxesFollowed();
  const instanceId = useModuleAxesInstanceId();
  const instances = useInstances();
  const toggleModuleAxisPending = useViewerStore((state) => state.toggleModuleAxisPending);
  const closeModuleAxes = useViewerStore((state) => state.closeModuleAxes);
  const setJointAxesMode = useViewerStore((state) => state.setJointAxesMode);
  const setJointAxesHoveredName = useViewerStore((state) => state.setJointAxesHoveredName);
  const { sendSaveModuleAxes, sendEnableJointAxes } = useWebSocket();

  // Deciding which way a joint should turn means looking at the axis, so
  // the overview comes on with the editor -- and goes back off with it if
  // that is where the user had left it.
  const overviewWasOn = useRef<boolean | null>(null);
  useEffect(() => {
    if (!open) {
      return;
    }
    if (overviewWasOn.current === null) {
      overviewWasOn.current = viewerStore.getState().jointAxesMode;
    }
    if (!viewerStore.getState().jointAxesMode) {
      setJointAxesMode(true);
      sendEnableJointAxes(true);
    }
    return () => {
      if (overviewWasOn.current === false) {
        setJointAxesMode(false);
        sendEnableJointAxes(false);
      }
      overviewWasOn.current = null;
      setJointAxesHoveredName(null);
    };
  }, [open, setJointAxesMode, setJointAxesHoveredName, sendEnableJointAxes]);

  const instance = instances.find((candidate) => candidate.instance_id === instanceId);
  const handleHover = (jointName: string | null) => {
    if (jointName === null || instance === undefined) {
      setJointAxesHoveredName(null);
      return;
    }
    setJointAxesHoveredName(uniqueJointName(instance.prefix, instance.instance_id, jointName));
  };

  if (!open) {
    return null;
  }

  const marked = Object.keys(pending);

  return (
    <Box
      p="sm"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 100,
        backgroundColor: "var(--mantine-color-body)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={600}>
            Module axes
          </Text>
          <Text size="xs" c="dimmed" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {moduleId}
          </Text>
        </Box>
        <Button size="compact-xs" variant="subtle" onClick={closeModuleAxes}>
          Close
        </Button>
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        Reversing writes the module&apos;s own URDF: its axis is negated and its
        limits swapped, and every instance already placed follows. Modules
        placed later come out reversed too. Point at a joint to find its axis
        in the viewport. The write is a change to the assets repository, and
        has to be committed there to outlive the next deploy.
      </Text>

      {error && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} p="xs" mb="xs">
          <Text size="xs">{error}</Text>
        </Alert>
      )}

      {/* The deployment restores the assets checkout on every deploy, so a
          write nobody commits lasts until the next one. Saying which file
          changed is the difference between a fix and a fix that vanishes. */}
      {writtenPath && (
        <Alert color="teal" variant="light" icon={<IconGitCommit size={16} />} p="xs" mb="xs">
          <Text size="xs">
            Written to <Text span ff="monospace">{writtenPath}</Text>, and {followed.length}{" "}
            joint(s) already placed followed it. Commit the file in the assets repository — a
            deploy restores that checkout, and an uncommitted change goes with it.
          </Text>
        </Alert>
      )}

      {!editable && (
        <Alert color="yellow" variant="light" p="xs" mb="xs">
          <Text size="xs">
            Read-only: writing a shared module takes admin access.
          </Text>
        </Alert>
      )}

      <ScrollArea style={{ flex: 1 }}>
        <Stack gap="xs">
          {joints.length === 0 && (
            <Text size="xs" c="dimmed">
              This module has no movable joint to reverse.
            </Text>
          )}
          {joints.map((joint) => (
            <JointRow
              key={joint.joint_name}
              joint={joint}
              pending={pending[joint.joint_name] === true}
              editable={editable}
              onToggle={toggleModuleAxisPending}
              onHover={handleHover}
            />
          ))}
        </Stack>
      </ScrollArea>

      <Group justify="space-between" mt="sm" wrap="nowrap">
        <Badge size="xs" variant="light" color={marked.length > 0 ? "orange" : "gray"}>
          {marked.length} marked
        </Badge>
        <Button
          size="xs"
          disabled={!editable || marked.length === 0 || moduleId === null}
          onClick={() => moduleId !== null && sendSaveModuleAxes(moduleId, marked)}
        >
          Save to module
        </Button>
      </Group>
    </Box>
  );
}
